import { useEffect, useRef } from "react";
import { useAuth } from "src/contexts/AuthContext";
import { posthog } from "src/initialisePostHog";

/**
 * Keeps the PostHog person in sync with the Firebase auth user: identifies every
 * visitor (including anonymous) by uid, and resets on user switch / sign-out so
 * a new Firebase user isn't merged with the previous one.
 */
export const usePostHogIdentity = (): void => {
  const { user, loading } = useAuth();
  const lastIdentifiedUid = useRef<string | null>(null);

  useEffect(() => {
    // Wait for the initial auth state check to complete.
    if (loading) return;

    if (!user) {
      if (lastIdentifiedUid.current) {
        posthog.reset();
        lastIdentifiedUid.current = null;
      }
      return;
    }

    // Switched to a different Firebase user — drop the old identity first.
    if (lastIdentifiedUid.current && lastIdentifiedUid.current !== user.uid) {
      posthog.reset();
    }

    posthog.identify(user.uid, {
      is_anonymous: user.isAnonymous,
      ...(user.email ? { email: user.email } : {}),
      ...(user.displayName ? { name: user.displayName } : {})
    });
    lastIdentifiedUid.current = user.uid;
  }, [user, loading]);
};
